// src/views/AccommodationProviderDashboard.js
import React from 'react';
import { Card, CardBody, CardTitle, Badge, Row, Col } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import ProviderFormWizard from '../../forms/ProviderFormWizard';

const AccommodationProviderDashboard = () => {
  const navigate = useNavigate();
  
  // Get provider information from localStorage (set during login)
  const providerType = localStorage.getItem('provider_type');
  const providerName = localStorage.getItem('provider_name');
  const representativeName = localStorage.getItem('representative_name');
  const regStatus = localStorage.getItem('reg_status');

  // Determine welcome message based on provider type
  const getWelcomeMessage = () => {
    if (providerType === 'individual') {
      return `Welcome, ${providerName || 'Individual Provider'}!`;
    } else if (providerType === 'entity') {
      return `Welcome to ${providerName || 'Your Organization'}!`;
    } else {
      return 'Welcome, Accommodation Provider!';
    }
  };

  const getSubtitleMessage = () => {
    if (providerType === 'entity' && representativeName) {
      return `Representative: ${representativeName}`;
    }
    return null;
  };

  const getProviderTypeDisplay = () => {
    if (providerType === 'individual') {
      return 'Individual Property Owner';
    } else if (providerType === 'entity') {
      return 'Business/Organization';
    }
    return 'Accommodation Provider';
  };

  const handlePropertyManagement = () => {
    navigate('/provider-property-management');
  };

  const getRegistrationStatusMessage = () => {
    switch (regStatus) {
      case '0':
        return {
          message: 'Please complete your registration to start managing properties',
          variant: 'warning'
        };
      case '1':
        return {
          message: 'We are reviewing your documents. You will be notified once approved.',
          variant: 'info'
        };
      case '2':
        return {
          message: 'Registration complete - You can now manage your properties',
          variant: 'success'
        };
      case '3':
        return {
          message: 'Verified Provider - Full access to all features',
          variant: 'primary'
        };
      default:
        return {
          message: 'Please complete your registration',
          variant: 'secondary'
        };
    }
  };

  const statusInfo = getRegistrationStatusMessage();

  return (
    <>
      {/* Show Welcome Card only when registration is complete */}
      {regStatus !== '0' && (
        <Card className="mb-4">
          <CardBody>
            <Row>
              <Col md="8">
                <CardTitle tag="h4" className="mb-2">
                  {getWelcomeMessage()}
                </CardTitle>
                {getSubtitleMessage() && (
                  <p className="text-muted mb-2">{getSubtitleMessage()}</p>
                )}
                <Badge color="info" className="me-2">
                  {getProviderTypeDisplay()}
                </Badge>
                <Badge color={statusInfo.variant}>
                  {statusInfo.message}
                </Badge>
              </Col>
              <Col md="4" className="text-end">
                <div className="h2 mb-0">
                  <i className={`bi ${providerType === 'entity' ? 'bi-building' : 'bi-house-door'}`}></i>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      )}

      {/* Registration Status Based Content */}
      {regStatus === '0' && (
        <ProviderFormWizard />
      )}

      {regStatus === '1' && (
        <Card className="text-center">
          <CardBody className="py-5">
            <div className="mb-4">
              <i className="bi bi-clock-history display-1 text-info"></i>
            </div>
            <CardTitle tag="h4" className="mb-3">
              Document Review in Progress
            </CardTitle>
            <p className="text-muted lead">
              We are currently reviewing your submitted documents. 
              You will be notified via email once the review process is complete.
            </p>
            <p className="text-muted">
              Thank you for your patience. This process typically takes 2-3 business days.
            </p>
          </CardBody>
        </Card>
      )}

      {regStatus === '2' && (
        <Row>
          <Col md="6">
            <Card className="mb-3">
              <CardBody>
                <CardTitle tag="h6">
                  <i className="bi bi-building me-2"></i>
                  My Properties
                </CardTitle>
                <p className="text-muted">Manage your accommodation listings</p>
                <button 
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => navigate('/provider-properties')}
                >
                  View/Add Properties
                </button>
              </CardBody>
            </Card>
          </Col>
          <Col md="6">
            <Card className="mb-3">
              <CardBody>
                <CardTitle tag="h6">
                  <i className="bi bi-people me-2"></i>
                  Current Bookings
                </CardTitle>
                <p className="text-muted">View and manage student bookings</p>
                <button className="btn btn-outline-primary btn-sm">
                  View Bookings
                </button>
              </CardBody>
            </Card>
          </Col>
          <Col md="6">
            <Card className="mb-3">
              <CardBody>
                <CardTitle tag="h6">
                  <i className="bi bi-cash-coin me-2"></i>
                  Payments
                </CardTitle>
                <p className="text-muted">Track payments and earnings</p>
                <button className="btn btn-outline-primary btn-sm">
                  View Payments
                </button>
              </CardBody>
            </Card>
          </Col>
          <Col md="6">
            <Card className="mb-3">
              <CardBody>
                <CardTitle tag="h6">
                  <i className="bi bi-gear me-2"></i>
                  Profile Settings
                </CardTitle>
                <p className="text-muted">Update your provider information</p>
                <button className="btn btn-outline-primary btn-sm">
                  Edit Profile
                </button>
              </CardBody>
            </Card>
          </Col>
          
          {/* Statistics Cards for regStatus === '2' */}
          <Col md="4">
            <Card className="text-center mb-3">
              <CardBody>
                <div className="h2 text-success">
                  <i className="bi bi-check-circle"></i>
                </div>
                <CardTitle tag="h6">Verified Provider</CardTitle>
                <p className="text-muted small">Your account is fully verified</p>
              </CardBody>
            </Card>
          </Col>
          <Col md="4">
            <Card className="text-center mb-3">
              <CardBody>
                <div className="h2 text-primary">
                  <i className="bi bi-building"></i>
                </div>
                <CardTitle tag="h6">Properties</CardTitle>
                <p className="text-muted small">0 active listings</p>
              </CardBody>
            </Card>
          </Col>
          <Col md="4">
            <Card className="text-center mb-3">
              <CardBody>
                <div className="h2 text-info">
                  <i className="bi bi-people"></i>
                </div>
                <CardTitle tag="h6">Students</CardTitle>
                <p className="text-muted small">0 current residents</p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {regStatus === '3' && (
        <Row>
          <Col md="6">
            <Card className="mb-3">
              <CardBody>
                <CardTitle tag="h6">
                  <i className="bi bi-building me-2"></i>
                  My Properties
                </CardTitle>
                <p className="text-muted">Manage your accommodation listings</p>
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate('/provider-properties')}
                >
                  View/Add Properties
                </button>
              </CardBody>
            </Card>
          </Col>
          <Col md="6">
            <Card className="mb-3">
              <CardBody>
                <CardTitle tag="h6">
                  <i className="bi bi-people me-2"></i>
                  Current Bookings
                </CardTitle>
                <p className="text-muted">View and manage student bookings</p>
                <button className="btn btn-primary btn-sm">
                  View Bookings
                </button>
              </CardBody>
            </Card>
          </Col>
          <Col md="6">
            <Card className="mb-3">
              <CardBody>
                <CardTitle tag="h6">
                  <i className="bi bi-cash-coin me-2"></i>
                  Payments
                </CardTitle>
                <p className="text-muted">Track payments and earnings</p>
                <button className="btn btn-primary btn-sm">
                  View Payments
                </button>
              </CardBody>
            </Card>
          </Col>
          <Col md="6">
            <Card className="mb-3">
              <CardBody>
                <CardTitle tag="h6">
                  <i className="bi bi-gear me-2"></i>
                  Profile Settings
                </CardTitle>
                <p className="text-muted">Update your provider information</p>
                <button className="btn btn-primary btn-sm">
                  Edit Profile
                </button>
              </CardBody>
            </Card>
          </Col>
          
          {/* Premium Features for Fully Verified Providers */}
          <Col md="4">
            <Card className="text-center mb-3 border-primary">
              <CardBody>
                <div className="h2 text-primary">
                  <i className="bi bi-award"></i>
                </div>
                <CardTitle tag="h6">Premium Provider</CardTitle>
                <p className="text-muted small">Enhanced features & priority listing</p>
              </CardBody>
            </Card>
          </Col>
          <Col md="4">
            <Card className="text-center mb-3">
              <CardBody>
                <div className="h2 text-success">
                  <i className="bi bi-building"></i>
                </div>
                <CardTitle tag="h6">Properties</CardTitle>
                <p className="text-muted small">0 active listings</p>
              </CardBody>
            </Card>
          </Col>
          <Col md="4">
            <Card className="text-center mb-3">
              <CardBody>
                <div className="h2 text-info">
                  <i className="bi bi-people"></i>
                </div>
                <CardTitle tag="h6">Students</CardTitle>
                <p className="text-muted small">0 current residents</p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default AccommodationProviderDashboard;